import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { backendUrl } from '../config/environment';
import './LoginSuccess.css';

interface CallbackResponse {
  status: string;
  message?: string;
  access_token?: string;
  user_info?: any;
}

const LoginSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code from URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        
        if (!code) {
          setError('Authorization code not found in callback URL');
          setLoading(false);
          return;
        }

        // Get session ID from localStorage
        const sessionId = localStorage.getItem('oauth_session_id');
        
        if (!sessionId) {
          setError('Session ID not found. Please start the login process again.');
          setLoading(false);
          return;
        }

        // Prepare callback data
        const callbackData = {
          code,
          state: state || '',
          session_id: sessionId
        };

        // Send callback to your backend
        const response = await axios.post<CallbackResponse>(
          `${backendUrl}/oauth/callback/shareIndia?session_id=${sessionId}`,
          callbackData,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data.status === 'success') {
          setSuccess(true);
          
          // Store access token if provided
          if (response.data.access_token) {
            localStorage.setItem('access_token', response.data.access_token);
          }
          
          // Store user info if provided
          if (response.data.user_info) {
            localStorage.setItem('user_info', JSON.stringify(response.data.user_info));
          }

          // Redirect to dashboard after 2 seconds
          // setTimeout(() => {
          //   navigate('/dashboard');
          // }, 2000);
        } else {
          setError(response.data.message || 'OAuth callback failed');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError('Failed to process OAuth callback. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="login-success-page">
        <div className="status-container">
          <div className="loading-spinner"></div>
          <h2>Processing OAuth Callback...</h2>
          <p>Please wait while we complete your authentication.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-success-page">
        <div className="status-container error">
          <div className="error-icon">❌</div>
          <h2>Authentication Failed</h2>
          <p className="error-message">{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-success-page">
        <div className="status-container success">
          <div className="success-icon">✅</div>
          <h2>Login Successful!</h2>
          <p>You have been successfully authenticated.</p>
          <p>Your session is now active and ready to use.</p>
          {/* <p>Redirecting to dashboard...</p>
          <div className="loading-spinner small"></div> */}
        </div>
      </div>
    );
  }

  return null;
};

export default LoginSuccess; 